import { Accordion, Button, Card, Table, Alert, Image, Row, Col, Form, FormGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import GuideImagesService from "../../services/guideImages.service";

import APP_CONF from "../../scripts/constants";
import GuideImagesForm from "../../components/forms/GuideImagesForm";
import PaginationComponent from "../../components/PaginationComponent";
import TableHeaderSortComponent from "../../components/TableHeaderSortComponent";

import UseAlertConfirm from "../../hooks/UseAlertConfirm";
import UserPermissionService from "../../services/userPermission.service";

const GuideImages = () => {

    const { t } = useTranslation();

    const { register, handleSubmit } = useForm({
        mode: "all",
    });

    const { showModal } = UseAlertConfirm();

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalItems: 0,
        itemsPerPage: APP_CONF.ITEMS_PER_PAGE,
        numPages: 1
    });

    const [filter, setFilter] = useState({
        active: ""
    })

    const [success, setSuccess] = useState({
        create: false,
        delete: false,
    });
    const [errors, setErrors] = useState({
        create: false,
        delete: false,
    });

    const [sortColumn, setSortColumn] = useState({
        sortByColumn: null,
        orderBy: null
    });
    const [guideImages, setGuideImages] = useState([]);
    const [indexDesc, setIndexDesc] = useState(false);
    const [permissions, setPermissions] = useState({});

    useEffect(() => {
        const loadData = async () => {
            await getPermissions();
        }

        loadData();
    }, [])

    useEffect(() => {
        getGuideImages();
        if (sortColumn.sortByColumn === 'id' && sortColumn.orderBy === 'desc') {
            setIndexDesc(true);
        } else {
            setIndexDesc(false);
        }
    }, [pagination.currentPage, filter, sortColumn]);

    const getPermissions = async () => {
        try {
            const data = await UserPermissionService.getPermissionsByConnected();
            setPermissions(data);
        } catch (error) {
            console.error(error);
        }
    }

    const getGuideImages = async () => {
        try {
            const data = await GuideImagesService.getAll(pagination, filter, sortColumn)
            setGuideImages(data.content);
            setPagination({
                ...pagination,
                totalItems: data.totalElements,
                numPages: data.totalPages
            });
        } catch ({ response }) {
            if (response?.data?.errorCode) {
                switch (response.data.errorCode) {
                    case 13002:
                        setErrors({ ...errors, email: true });
                        break;
                    default:
                        setErrors({ ...errors, create: true });
                }
            }
        }
    }

    const createGuideImage = async (data) => {
        setErrors({ ...errors, create: false });
        setSuccess({ ...success, create: false });
        try {
            await GuideImagesService.create(data);
            setSuccess({ ...success, create: true });
            getGuideImages();
        } catch (e) {
            console.error(e.response);
            setErrors({ ...errors, create: true });
        }
    };

    const deleteGuideImage = async (id) => {

        showModal({
            title: t("general.delete.title"),
            body: t("general.delete.message"),
            confirmButtonVariant: "danger",
            onConfirm: async () => {
                setErrors({ ...errors, create: false });
                setSuccess({ ...success, create: false });
                try {
                    await GuideImagesService.remove(id);
                    setSuccess({ ...success, delete: true });
                    getGuideImages();
                } catch (e) {
                    console.error(e.response);
                    setErrors({ ...errors, delete: true });
                }
            }
        });

    }

    const filterSearch = (data) => {
        let obj = data;
        if (obj.search === "") {
            obj = {
                active: data.active
            }
        }
        setFilter(obj);
    }

    return (
        <>
            <div className={"d-sm-flex align-items-center justify-content-between mb-4"}>
                <h1 className={"h3 mb-0 text-gray-800"}>{t('guideImages.title')}</h1>
            </div>

            <Alert variant={"success"} show={success.create} dismissible onClose={() => setSuccess({ ...success, create: false })}>
                {t('guideImages.success.create')}
            </Alert>

            <Alert variant={"danger"} show={errors.create} dismissible onClose={() => setErrors({ ...errors, create: false })}>
                {t('guideImages.error.create')}
            </Alert>

            <Alert variant={"success"} show={success.delete} dismissible onClose={() => setSuccess({ ...success, delete: false })}>
                {t('guideImages.success.delete')}
            </Alert>

            <Alert variant={"danger"} show={errors.delete} dismissible onClose={() => setErrors({ ...errors, delete: false })}>
                {t('guideImages.error.delete')}
            </Alert>

            {permissions.guideImages?.create ? 
            <Card className={"shadow mb-4"}>
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header><h6 className={"m-0 fw-bold text-primary"}>{t('guideImages.creation.title')}</h6>
                        </Accordion.Header>
                        <Accordion.Body>
                            <GuideImagesForm onSubmit={createGuideImage} permissions={permissions} />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Card>
            : null}

            <Card className={"shadow mb-4"}>
                <Card.Header className={"py-3"}>
                    <h6 className={"m-0 fw-bold text-primary"}>{t('guideImages.list.title')}</h6>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit(filterSearch)}>
                        <Row>
                            <Col lg="2" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('guideImages.filter.active.title')}</Form.Label>
                                    <Form.Select {...register('active')} aria-label="Default select example">
                                        <option value="">{t('guideImages.filter.active.value1')}</option>
                                        <option value={true}>{t('guideImages.filter.active.value2')}</option>
                                        <option value={false}>{t('guideImages.filter.active.value3')}</option>
                                    </Form.Select>
                                </FormGroup>
                            </Col>
                            <Col lg="3" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('guideImages.filter.search')}</Form.Label>
                                    <Form.Control type="text" {...register("search")} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Button type="submit" className={"mt-3"} disabled={!permissions.guideImages?.read}>{t('guideImages.filter.button')}</Button>
                    </Form>
                    <hr />
                    <Table responsive>
                        <thead>
                        <tr>
                            <TableHeaderSortComponent scope={"col"} text={t('guideImages.list.fields.index')} column={"id"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                            <TableHeaderSortComponent scope={"col"} text={t('guideImages.list.fields.title1')} column={"title1"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                            <TableHeaderSortComponent scope={"col"} text={t('guideImages.list.fields.title2')} column={"title2"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                            <th scope="col">{t('guideImages.list.fields.image')}</th>
                            <TableHeaderSortComponent scope={"col"} text={t('guideImages.list.fields.active')} column={"active"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {guideImages.length > 0 ? guideImages.map((guideImage, index) => (
                                <tr key={index}>
                                    {indexDesc ? <td>{guideImages.length - index}</td> : <td>{index + 1}</td>}
                                    <td>
                                        <div className="d-flex flex-row flex-nowrap">
                                            {Object.values(guideImage.title1).map((translation, i) =>
                                                <div key={i} className="me-2">
                                                    <Image src={translation.language.icon} className="me-2 flags-img-sm" />
                                                    {i + 1 === Object.values(guideImage.title1).length ? translation.value : translation.value + ' | '}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex flex-row flex-nowrap">
                                            {Object.values(guideImage.title2).map((translation, i) =>
                                                <div key={i} className="me-2">
                                                    <Image src={translation.language.icon} className="me-2 flags-img-sm" />
                                                    {i + 1 === Object.values(guideImage.title2).length ? translation.value : translation.value + ' | '}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td><Image fluid thumbnail src={guideImage.imageUrl} className="flags-img-md" /></td>
                                    <td>{guideImage.active ? 'Si' : 'No'}</td>
                                    <td className={"text-center"}>
                                        <Button variant="link" size="sm" className="ms-2" to={`/guide-images/${guideImage.id}`} as={Link} disabled={!permissions.guideImages?.read}>
                                            <FontAwesomeIcon icon="edit" />
                                        </Button>
                                        <Button variant="link" size="sm" className="text-danger" onClick={() => deleteGuideImage(guideImage.id)} disabled={!permissions.guideImages?.delete}>
                                            <FontAwesomeIcon icon="trash" />
                                        </Button>
                                    </td>
                                </tr>
                            )) :
                            <tr>
                                <td colSpan={4}>{t('general.empty')}</td>
                            </tr>
                        }
                        </tbody>
                    </Table>
                    <PaginationComponent pagination={pagination}
                                         setPagination={setPagination}
                                         alwaysShown={guideImages.length > 0} />
                </Card.Body>
            </Card>
        </>
    );
}

export default GuideImages;